/**
 * Focus Management Utilities
 * Handles programmatic focus for smooth scroll navigation
 */

/**
 * Focuses an element after smooth scroll completes
 * Makes element temporarily focusable if needed
 */
export const focusElementAfterScroll = (
  targetId: string,
  scrollDuration = 100
): void => {
  const target = document.getElementById(targetId);
  if (!target) {
    console.warn(`Focus target not found: ${targetId}`);
    return;
  }
  
  // Make element focusable if not already
  const hadTabIndex = target.hasAttribute('tabindex');
  const originalTabIndex = target.getAttribute('tabindex');
  
  if (!hadTabIndex) {
    target.setAttribute('tabindex', '-1');
  }
  
  // Remove tabindex and outline on blur (cleanup)
  const handleBlur = () => {
    if (!hadTabIndex) {
      target.removeAttribute('tabindex');
    } else if (originalTabIndex) {
      target.setAttribute('tabindex', originalTabIndex);
    }
    target.removeEventListener('blur', handleBlur);
  };
  
  target.addEventListener('blur', handleBlur);
  
  // Focus after scroll animation completes
  setTimeout(() => {
    target.focus();
    
    // Announce to screen readers
    const announcement = target.getAttribute('aria-label') || 
                        target.textContent?.trim() || 
                        'Section loaded';
    announceToScreenReader(announcement);
  }, scrollDuration);
};

/**
 * Announces text to screen readers without visual change
 */
export const announceToScreenReader = (message: string): void => {
  const announcement = document.createElement('div');
  announcement.setAttribute('role', 'status');
  announcement.setAttribute('aria-live', 'polite');
  announcement.className = 'visually-hidden';
  announcement.textContent = message;

  document.body.appendChild(announcement);

  // Remove after announcement
  setTimeout(() => {
    // Check if element still exists and is a child of body before removing
    if (announcement.parentNode === document.body) {
      document.body.removeChild(announcement);
    }
  }, 1000);
};
